export const toolkitHighlights = [
  "6 practical clinical volumes",
  "5 exclusive professional bonuses",
  "Clinical decision trees and assessment algorithms",
  "Ready-to-use consultation checklists",
  "Differential diagnosis matrices",
  "Patient assessment and follow-up templates",
];

export const patientVignettes = [
  "One patient reports irregular periods and disrupted sleep.",
  "Another presents with mood changes, fatigue, and persistent vasomotor symptoms.",
  "A third arrives with multiple laboratory results, conflicting information, and questions about hormone therapy.",
];

export const beforeItems = [
  "Symptoms assessed without a consistent consultation structure.",
  "Difficulty distinguishing menopausal transition symptoms from alternative conditions.",
  "Uncertainty about when laboratory testing is clinically appropriate.",
  "Risk factors and contraindications documented inconsistently.",
  "Management options reviewed without a clear decision framework.",
  "Follow-up plans that require rebuilding the process for every patient.",
];

export const afterItems = [
  "Follow a repeatable five-step assessment pathway.",
  "Organize symptoms, menstrual history, and relevant clinical findings.",
  "Use differential diagnosis tools to identify conditions requiring further evaluation.",
  "Document patient-specific risk factors and management considerations.",
  "Structure shared decision-making discussions.",
  "Create a clear assessment summary and follow-up plan.",
];

export const painPoints = [
  {
    title: "The symptoms overlap.",
    body: "Your patient reports fatigue, sleep disturbances, mood changes, and menstrual irregularities. You need a systematic way to assess possible explanations.",
  },
  {
    title: "Laboratory results create more questions.",
    body: "You're trying to determine whether testing is indicated and how results should be interpreted in the context of a changing menstrual cycle.",
  },
  {
    title: "Every consultation feels different.",
    body: "You want a consistent framework without reducing individualized clinical care to a rigid formula.",
  },
  {
    title: "Hormone therapy discussions become complicated.",
    body: "You need to organize eligibility considerations, contraindications, alternatives, and shared decision-making.",
  },
  {
    title: "Documentation takes too much mental effort.",
    body: "You want assessment templates that help capture the relevant information without starting from scratch.",
  },
  {
    title: "Follow-up planning lacks a repeatable structure.",
    body: "You need a practical method for recording treatment discussions, monitoring considerations, and reassessment triggers.",
  },
];

export const pathwaySteps = [
  {
    step: "Identify",
    body: "Collect the relevant symptoms, menstrual history, medical background, and patient concerns.",
  },
  {
    step: "Differentiate",
    body: "Organize possible explanations, red flags, and indications for additional evaluation.",
  },
  {
    step: "Assess",
    body: "Review clinical risk factors, relevant investigations, contraindications, and patient-specific considerations.",
  },
  {
    step: "Plan",
    body: "Structure evidence-informed management discussions and individualized care planning.",
  },
  {
    step: "Document & Follow Up",
    body: "Record the assessment, shared decisions, monitoring considerations, and reassessment plan.",
  },
];

export const volumes = [
  {
    title: "Perimenopause Initial Assessment Blueprint™",
    tagline: "Turn a scattered first consultation into a structured clinical assessment.",
    items: [
      "Initial consultation workflow",
      "Menstrual and symptom history checklist",
      "Clinical background assessment template",
      "Red-flag screening guide",
      "Patient concern documentation sheet",
      "Structured first-visit summary",
    ],
    outcome:
      "A reusable framework for collecting and organizing the information needed for an initial assessment.",
  },
  {
    title: "Perimenopause Differential Diagnosis Decision Map™",
    tagline: "Organize overlapping symptoms without automatically attributing them to perimenopause.",
    items: [
      "Differential diagnosis decision trees",
      "Symptom-pattern assessment matrices",
      "Abnormal bleeding evaluation considerations",
      "Alternative-cause screening checklists",
      "Investigation and referral decision aids",
      "Clinical reasoning documentation templates",
    ],
    outcome:
      "A structured way to identify possible alternative explanations and determine when further assessment is warranted.",
  },
  {
    title: "Hormonal Testing & Clinical Interpretation Roadmap™",
    tagline: "Know how to organize testing decisions in the context of the individual patient.",
    items: [
      "Clinical indications for considering investigations",
      "Testing decision pathways",
      "Hormonal variability interpretation notes",
      "Contextual laboratory review templates",
      "Common interpretation pitfalls",
      "Investigation documentation checklists",
    ],
    outcome:
      "A more consistent framework for deciding when testing may be appropriate and documenting results in context.",
  },
  {
    title: "Menopause Hormone Therapy Eligibility Assessment Blueprint™",
    tagline: "Structure the clinical evaluation before considering hormone therapy.",
    items: [
      "Eligibility assessment workflow",
      "Contraindication screening checklist",
      "Relevant risk-factor review matrix",
      "Treatment option discussion framework",
      "Shared decision-making documentation template",
      "Referral and specialist consultation considerations",
    ],
    outcome: "An organized assessment and discussion process grounded in current clinical guidance.",
  },
  {
    title: "Perimenopause Management Planning Decision Tree™",
    tagline: "Turn your assessment findings into a documented management discussion.",
    items: [
      "Management planning decision tree",
      "Symptom-priority assessment worksheet",
      "Hormonal and nonhormonal option comparison framework",
      "Patient preference documentation tools",
      "Individualized care planning templates",
      "Reassessment and escalation considerations",
    ],
    outcome: "A structured framework for documenting individualized management decisions.",
  },
  {
    title: "Perimenopause Follow-Up & Monitoring Blueprint™",
    tagline: "Build a consistent follow-up workflow without overlooking important clinical considerations.",
    items: [
      "Follow-up consultation checklist",
      "Symptom reassessment templates",
      "Treatment response documentation framework",
      "Adverse-effect review checklist",
      "Monitoring and referral considerations",
      "Longitudinal clinical summary template",
    ],
    outcome:
      "A repeatable approach to documenting follow-up, patient concerns, and the need for reassessment.",
  },
];

export const clinicalTools = [
  { name: "The 5-Step Clinical Decision Map", body: "A visual overview of the assessment-to-follow-up process." },
  { name: "Perimenopause Assessment Checklist", body: "A structured tool for gathering relevant clinical information." },
  {
    name: "Differential Diagnosis Matrix",
    body: "A practical reference for organizing overlapping symptoms and alternative explanations.",
  },
  {
    name: "Hormone Therapy Risk Review Sheet",
    body: "A documentation aid for reviewing relevant clinical considerations.",
  },
  {
    name: "Individualized Management Plan Template",
    body: "A reusable framework for recording decisions, patient preferences, and follow-up considerations.",
  },
];

export const bonuses = [
  {
    title: "Perimenopause First Consultation Toolkit™",
    value: 19,
    image: "https://cdn.magicpatterns.com/patterns/generated-images/e868b43e-1057-45d7-8d5d-f9df38bb893b.jpg",
    body: "A collection of editable first-visit assessment forms, symptom history sheets, clinical intake checklists, and documentation templates.",
    note: "Designed to help organize the initial consultation.",
  },
  {
    title: "Abnormal Uterine Bleeding Triage Decision Map™",
    value: 17,
    image: "https://cdn.magicpatterns.com/patterns/generated-images/672c52b3-13f1-4482-8256-256e29f76718.jpg",
    body: "A focused decision-support resource for organizing the assessment of abnormal bleeding patterns and identifying situations requiring additional investigation or referral.",
    note: "Includes red-flag checklists and documentation aids.",
  },
  {
    title: "Hormone Therapy Shared Decision-Making Toolkit™",
    value: 19,
    image: "https://cdn.magicpatterns.com/patterns/generated-images/b5878d6e-4c5f-4bff-a09a-da5371f47023.jpg",
    body: "Structured discussion worksheets covering potential benefits, limitations, risks, alternatives, and patient preferences.",
    note: "Includes a consultation documentation template.",
  },
  {
    title: "Perimenopause Symptom Tracking & Follow-Up Pack™",
    value: 15,
    image: "https://cdn.magicpatterns.com/patterns/generated-images/c6f5749d-3cc6-4710-b09a-ea4572dad906.jpg",
    body: "Practical symptom tracking sheets, follow-up assessment forms, and longitudinal documentation templates.",
    note: "Designed to support ongoing clinical review.",
  },
  {
    title: "Perimenopause Clinical Case Mapping Workbook™",
    value: 27,
    image: "https://cdn.magicpatterns.com/patterns/generated-images/e64936c8-b149-47c2-8172-d13fe1be3b1e.jpg",
    body: "A workbook with fictional educational case scenarios and guided exercises for practicing the five-step assessment framework.",
    note: "Includes decision worksheets and structured documentation exercises.",
  },
];

export const valueStack = [
  { item: "6-Volume Perimenopause Clinical Decision Roadmap™", value: 97 },
  { item: "Bonus 1: First Consultation Toolkit™", value: 19 },
  { item: "Bonus 2: Abnormal Uterine Bleeding Triage Decision Map™", value: 17 },
  { item: "Bonus 3: Shared Decision-Making Toolkit™", value: 19 },
  { item: "Bonus 4: Symptom Tracking & Follow-Up Pack™", value: 15 },
  { item: "Bonus 5: Clinical Case Mapping Workbook™", value: 27 },
];

export const offerIncludes = [
  "All 6 clinical volumes",
  "All 5 exclusive bonuses",
  "Downloadable digital resources",
  "Instant access after purchase",
  "7-day money-back guarantee",
];

export const testimonials = [
  {
    quote:
      "I used to rebuild my first-visit notes for every perimenopause patient. Volume 1 and the intake checklist gave me a consistent structure I now use in every consultation. My notes are cleaner and nothing important gets skipped.",
    name: "Dr. Elena Rodríguez",
    role: "Gynecologist · Texas, USA",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/fbae6a2d-17ef-40d0-b034-615b12cff269.jpg",
  },
  {
    quote:
      "As a family physician, I see these patients between a dozen other complaints. The differential diagnosis matrix is the page I keep printed on my desk. It keeps me from attributing everything to the transition too quickly.",
    name: "Dr. Michael Harlan",
    role: "Family Physician · Ohio, USA",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/4677e51b-e974-4f21-adf3-de62ca9a1a92.jpg",
  },
  {
    quote:
      "The hormone therapy eligibility workflow and the shared decision-making template made those longer conversations far more organized. Patients appreciate seeing the options laid out clearly.",
    name: "Dr. Amara Okafor",
    role: "Internal Medicine Physician · London, UK",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/d56c80c7-0bce-40e7-b0c2-5da262ddbf37.jpg",
  },
  {
    quote:
      "For $27 I expected a short PDF. Instead I got a full set of templates I could adapt to our clinic's documentation. The follow-up pack alone saved me hours setting up reassessment visits.",
    name: "Grace Lin, NP",
    role: "Women's Health Nurse Practitioner · Toronto, Canada",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/8a0c0266-c883-4a9f-a2dd-d9a73486e6fd.jpg",
  },
  {
    quote:
      "After 25 years in practice, I didn't need more theory — I needed a clean workflow. The five-step pathway matches how I already think, and now my residents can follow the same process.",
    name: "Dr. Susan Whitfield",
    role: "OB-GYN · Melbourne, Australia",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/d8ee8d32-0b4c-47a3-a61a-5c482d6cec2c.jpg",
  },
];

export const faqs = [
  {
    q: "How will I receive the collection?",
    a: "Right after completing your purchase, you'll receive digital access to the Perimenopause Clinical Decision Roadmap™ and all included resources, sent to the email address you use at checkout.",
  },
  {
    q: "Is this resource suitable for healthcare professionals in the United States?",
    a: "The content is written in American English for an international professional audience, including U.S.-based clinicians. Clinical decisions must always follow applicable local regulations, professional standards, current guidelines, and individual patient circumstances.",
  },
  {
    q: "Who is this roadmap designed for?",
    a: "It's intended for qualified healthcare professionals involved in women's health, including gynecologists, family physicians, and other clinicians whose scope of practice includes perimenopause assessment and management. It is not intended for independent patient self-diagnosis or self-treatment.",
  },
  {
    q: "Does this resource teach me how to prescribe hormone therapy?",
    a: "The roadmap provides educational decision-support frameworks for reviewing clinical eligibility considerations, contraindications, treatment options, and documentation. It does not replace medical training, prescribing qualifications, current clinical guidelines, or patient-specific professional judgment.",
  },
  {
    q: "Can I download and print the materials?",
    a: "Yes. The collection includes downloadable digital reference materials, checklists, and templates that can be printed for professional use.",
  },
  {
    q: "Is this a subscription?",
    a: "No. The complete collection is available for a one-time payment of $27 USD. The price is automatically converted to your local currency at checkout.",
  },
  {
    q: "Does it include clinical case examples?",
    a: "Yes. The Clinical Case Mapping Workbook includes fictional educational scenarios designed to help you practice organizing assessments and management considerations.",
  },
  {
    q: "What if the materials aren't right for me?",
    a: "Your purchase includes a seven-day money-back guarantee, subject to the published refund policy.",
  },
  {
    q: "Can the roadmap replace clinical guidelines?",
    a: "No. It is a supplementary educational resource. You should consult current evidence-based guidelines, applicable regulations, and relevant specialist recommendations when making clinical decisions.",
  },
];

export const finalIncludes = [
  "6 clinical volumes",
  "5 exclusive professional bonuses",
  "Clinical algorithms and decision trees",
  "Practical checklists and templates",
  "Instant digital access",
  "One-time payment",
  "7-day money-back guarantee",
];

export const recentBuyers = [
  { name: "Dra. Lucía Fernández", country: "Mexico" },
  { name: "Dr. Andrés Morales", country: "Colombia" },
  { name: "Dra. Valentina Rojas", country: "Chile" },
  { name: "Dra. Camila Herrera", country: "Argentina" },
  { name: "Dr. Javier Castillo", country: "Peru" },
  { name: "Dra. Mariana López", country: "Spain" },
  { name: "Dra. Gabriela Suárez", country: "Ecuador" },
  { name: "Dra. Daniela Vargas", country: "Costa Rica" },
  { name: "Dr. Ricardo Méndez", country: "Dominican Republic" },
  { name: "Dra. Paula Jiménez", country: "Uruguay" },
  { name: "Dra. Sofía Ramírez", country: "Guatemala" },
  { name: "Dra. Isabel Torres", country: "Puerto Rico" },
  { name: "Dra. Natalia Acosta", country: "Paraguay" },
  { name: "Dr. Fernando Quispe", country: "Bolivia" },
  { name: "Dra. Carolina Ortiz", country: "Panama" },
];
